/** Ejecutor común de acciones de flujo del servidor (design.md §22.5).
 *
 * Recibe las acciones del contexto (`WorkflowContextAction`: code/label/
 * requires_reason/enabled/disabled_reason), pide el motivo solo cuando
 * `requires_reason`, propaga la revisión del documento y usa
 * `Idempotency-Key` (en la capa de servicio). Un 409 exige recarga explícita.
 */
import { useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';
import { MobileButton } from './MobileButton';
import { MobileIcon } from './MobileIcon';
import { extractRevisionConflict } from '@/services/mobileApiClient';
import type { WorkflowContextAction } from '@/services/tasksService';

export function isServerActionDisabled(action: WorkflowContextAction): boolean {
  if (action.disabled === true) return true;
  if (action.enabled === false) return true;
  return false;
}

export function serverActionDisabledReason(action: WorkflowContextAction): string | null {
  return action.disabled_reason ?? null;
}

type ServerActionRunnerProps = {
  actions: WorkflowContextAction[];
  /** Revisión del documento/contexto para X-Expected-Document-Revision. */
  documentRevision?: number;
  /** Códigos permitidos en esta pantalla (whitelist); ausente = todas. */
  allowed?: string[];
  /** Texto del motivo pedido cuando la acción lo exige. */
  reasonLabel?: string;
  onExecute: (
    actionCode: string,
    body: { reason?: string },
    revision?: number,
  ) => Promise<unknown>;
  onSuccess?: (actionCode: string, result: unknown) => void;
  /** Recarga explícita tras un 409 (el runner no conoce el refetch). */
  onConflictReload?: () => void;
};

function actionKey(action: WorkflowContextAction): string {
  return action.action_code ?? action.code;
}

export function ServerActionRunner({
  actions,
  documentRevision,
  allowed,
  reasonLabel = 'Motivo',
  onExecute,
  onSuccess,
  onConflictReload,
}: ServerActionRunnerProps) {
  const [pending, setPending] = useState<WorkflowContextAction | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const visible = allowed ? actions.filter((action) => allowed.includes(actionKey(action))) : actions;

  if (visible.length === 0) return null;

  function openAction(action: WorkflowContextAction) {
    setPending(action);
    setReason('');
    setReasonError(null);
    setFailed(null);
    setConflict(false);
  }

  function closeDialog() {
    if (busy) return;
    setPending(null);
    setReason('');
    setReasonError(null);
    setFailed(null);
    setConflict(false);
  }

  async function confirmAction() {
    if (!pending || busy) return;
    const code = actionKey(pending);
    if (pending.requires_reason && reason.trim().length === 0) {
      setReasonError('El motivo es obligatorio');
      return;
    }
    setReasonError(null);
    setFailed(null);
    setConflict(false);
    setBusy(true);
    try {
      const body = pending.requires_reason ? { reason: reason.trim() } : {};
      const result = await onExecute(code, body, documentRevision);
      onSuccess?.(code, result);
      setPending(null);
      setReason('');
    } catch (error) {
      if (extractRevisionConflict(error)) {
        setConflict(true);
        setFailed('Los datos cambiaron en el servidor. Recarga para ver el estado actual.');
      } else {
        setFailed(error instanceof Error ? error.message : 'No se pudo ejecutar la acción');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      {visible.map((action) => {
        const code = actionKey(action);
        const disabled = isServerActionDisabled(action);
        const disabledReason = serverActionDisabledReason(action);
        return (
          <div key={code}>
            <MobileButton
              variant={code === 'cancel' ? 'danger' : 'primary'}
              size="md"
              fullWidth
              disabled={disabled}
              icon={<MobileIcon name={code === 'cancel' ? 'cancel' : 'send'} className="h-5 w-5" />}
              onClick={() => openAction(action)}
            >
              {action.label}
            </MobileButton>
            {disabled && disabledReason && (
              <p className="mt-1 break-words px-1 text-xs text-text-tertiary">{disabledReason}</p>
            )}
          </div>
        );
      })}

      {pending && !conflict && (
        <ConfirmDialog
          title={pending.label}
          message={
            pending.requires_reason
              ? 'Esta acción registra el motivo en la auditoría.'
              : `¿Confirmas "${pending.label}"?`
          }
          confirmLabel="Confirmar"
          danger={actionKey(pending) === 'cancel'}
          busy={busy}
          error={failed}
          reason={
            pending.requires_reason
              ? {
                  label: reasonLabel,
                  value: reason,
                  onChange: (next: string) => {
                    setReason(next);
                    if (reasonError && next.trim().length > 0) setReasonError(null);
                  },
                  error: reasonError ?? undefined,
                }
              : undefined
          }
          onConfirm={() => void confirmAction()}
          onCancel={closeDialog}
        />
      )}

      {pending && conflict && (
        <ConfirmDialog
          title="Datos desactualizados"
          message={failed ?? 'Los datos cambiaron en el servidor.'}
          confirmLabel="Recargar"
          busy={busy}
          onConfirm={() => {
            setPending(null);
            setConflict(false);
            onConflictReload?.();
          }}
          onCancel={closeDialog}
        />
      )}
    </div>
  );
}
